import { ref } from 'vue';
import { defineStore } from 'pinia';

import apiGw from '@/services/apiGw';
import { Plan } from '@/components/billing/types/plan';
import { useContextSwitchStore } from '@/store/contextSwitchStore';
import { PlanFeatures } from '@/components/billing/types/subscription';
import { createResourceLoadingMixin } from '@/store/resourceLoadingMixin';

export const useSubscriptionsStore = defineStore('subscriptionsStore', () => {
  const contextSwitchStore = useContextSwitchStore();
  const resourceLoadingMixin = createResourceLoadingMixin();

  // STATES
  // Give access to all resources loaded via the resourceLoadingMixin in the store (makes it visible in the devtools)
  const resources = ref<Map<string, any>>(resourceLoadingMixin.resources.value);

  // const plansDetails = ref<{ [code: string]: Plan }>({});

  // GETTERS
  // ...

  // ACTIONS
  function $reset(): void {
    resourceLoadingMixin.reset();
  }

  async function loadSubscriptionForContext(ctxUid: string, forceReload?: boolean) {
    const uri = `/v2/agent/${ctxUid}/subscription`;
    if (forceReload) {
      resourceLoadingMixin.removeResourcePaginated(uri);
    }

    return resourceLoadingMixin.loadResource(
      uri,
      async () => {
        const response = await apiGw.get(uri);
        return response.data;
      },
      forceReload
    );
  }

  async function loadPlan(planCode: string, forceReload?: boolean): Promise<Plan> {
    if (forceReload) {
      resourceLoadingMixin.removeResourcePaginated(`plan-${planCode}`);
    }

    return resourceLoadingMixin.loadResource(
      `plan-${planCode}`,
      async () => {
        const response = await apiGw.get(`/extension/plans/${planCode}`);
        return response.data.plan;
      },
      forceReload
    );
  }

  async function currentContextHasFeature(feature: PlanFeatures): Promise<boolean> {
    const ctxUid = contextSwitchStore.currentContextUid;
    if (!ctxUid) {
      console.error('No context UID found');
      return false;
    }

    const subscription = await loadSubscriptionForContext(ctxUid);
    if (!subscription) {
      console.error(`No subscription found for context ${ctxUid}`);
      return false;
    }

    const plan = await loadPlan(subscription.current.planCode);

    const featureKeys = Object.keys(plan.features);
    return featureKeys.includes(feature);
  }

  return {
    // STATES
    resources,
    // GETTERS
    // ...
    // ACTIONS
    $reset,
    loadPlan,
    loadSubscriptionForContext,
    currentContextHasFeature
  };
});
